import UPNG from 'upng-js';
import piexif from 'piexifjs';
import { mountImageBatch } from './image-batch-queue.js';
import { canvasToBlob, imageExtension, imageIsAr, imageName, makeCanvas, openBitmap } from './image-batch-utils.js';
import { trackEvent } from '../core/analytics.js';

const t = (ar, en) => imageIsAr ? ar : en;

const fields = [
  {
    key: 'format',
    label: t('صيغة الإخراج', 'Output format'),
    type: 'select',
    default: 'auto',
    options: [
      { value: 'auto', label: t('نفس صيغة الملف الأصلية (تلقائي)', 'Original format (Auto)') },
      { value: 'image/webp', label: 'WebP (حديث وأصغر حجماً)' },
      { value: 'image/jpeg', label: 'JPEG / JPG' },
      { value: 'image/png', label: 'PNG' }
    ]
  },
  {
    key: 'quality',
    label: t('جودة الصورة %', 'Image Quality %'),
    hint: t('النسبة المثالية الموصى بها هي 70% إلى 80% لتحقيق أصغر حجم مع وضوح فائق', 'Recommended 70%–80% for maximum size reduction with crystal clarity'),
    type: 'range',
    min: 10,
    max: 100,
    step: 1,
    default: 100,
    suffix: '%'
  },
  {
    key: 'maxDimension',
    label: t('أقصى أبعاد للصورة', 'Max dimensions'),
    hint: t('تصغير أبعاد الصور الضخمة (كاميرا الهاتف) يوفر حتى 90% من المساحة', 'Downscaling oversized camera photos saves up to 90% space'),
    type: 'select',
    default: 'original',
    options: [
      { value: 'original', label: t('الأبعاد الأصلية (بدون تغيير المقاس)', 'Original dimensions (No scaling)') },
      { value: '2560', label: t('2560px — دقة فائقة (2K QHD)', '2560px — Ultra HD (2K)') },
      { value: '1920', label: t('1920px — دقة عالية كاملة (Full HD)', '1920px — Full HD (Recommended)') },
      { value: '1280', label: t('1280px — مناسب للمواقع والشبكات (HD)', '1280px — Web & Social (HD)') },
      { value: '800', label: t('800px — مساحة خفيفة ومصغرات', '800px — Lightweight thumbnails') }
    ]
  },
  {
    key: 'stripExif',
    label: t('حذف بيانات EXIF والخصوصية', 'Strip EXIF metadata'),
    hint: t('إزالة بيانات الموقع الجغرافي ونوع الكاميرا وتاريخ الالتقاط لتقليل الحجم وحماية الخصوصية', 'Removes GPS location, camera model and timestamps to reduce size and protect privacy'),
    type: 'checkbox',
    default: true,
    span: 'full'
  }
];

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function dataUrlToBlob(dataUrl) {
  const parts = dataUrl.split(',');
  const mime = parts[0].match(/:(.*?);/)[1];
  const bstr = atob(parts[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new Blob([u8arr], { type: mime });
}

function resolveMime(file, requested) {
  if (requested && requested !== 'auto') return requested;
  const ext = file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase();
  if (file.type === 'image/png' || ext === 'png') return 'image/png';
  if (file.type === 'image/webp' || ext === 'webp') return 'image/webp';
  if (file.type === 'image/jpeg' || ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (file.type === 'image/avif' || ext === 'avif') return 'image/webp';
  if (file.type && file.type.startsWith('image/')) return file.type;
  return 'image/jpeg';
}

mountImageBatch({
  rootId: 'compressor-app',
  autoProcess: true,
  fields,
  zipName: 'compressed-images.zip',
  process: async (file, settings, report) => {
    report(10, t('جارٍ فحص الصورة', 'Analyzing image'));
    const bitmap = await openBitmap(file);
    let width = bitmap.width;
    let height = bitmap.height;

    // Optional Intelligent Downscaling for massive photos
    const maxDim = settings.maxDimension === 'original' || !settings.maxDimension
      ? Infinity
      : Number(settings.maxDimension);

    if (Math.max(width, height) > maxDim) {
      const ratio = maxDim / Math.max(width, height);
      width = Math.max(1, Math.round(width * ratio));
      height = Math.max(1, Math.round(height * ratio));
    }

    const type = resolveMime(file, settings.format);
    const { canvas, context } = makeCanvas(width, height, type === 'image/jpeg' ? '#ffffff' : null);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    report(45, t('جارٍ ضغط الصورة متعدد المراحل', 'Multi-pass image compression'));

    async function encodeAtQuality(q) {
      if (type === 'image/png') {
        const imgData = context.getImageData(0, 0, width, height);
        // When quality is >= 0.98, lossless PNG (cnum = 0)
        // When quality < 0.98, smart lossy palette quantization (TinyPNG style, 16..256 colors)
        const cnum = q >= 0.98 ? 0 : Math.max(16, Math.min(256, Math.round(q * 256)));
        const pngBuffer = UPNG.encode([imgData.data.buffer], width, height, cnum);
        return new Blob([pngBuffer], { type: 'image/png' });
      }
      return await canvasToBlob(canvas, type, q);
    }

    const userQuality = Number(settings.quality || 100) / 100;
    let currentQ = userQuality;
    let blob = await encodeAtQuality(currentQ);

    // Adaptive Pass 1: If encoded size is >= original and user quality was > 0.45,
    // automatically search for the lower quantization threshold to beat original size
    if (blob.size >= file.size && currentQ > 0.45) {
      report(70, t('تحسين ذكي لتقليل الحجم', 'Adaptive size optimization'));
      const altQ = Math.max(0.35, currentQ - 0.20);
      const altBlob = await encodeAtQuality(altQ);
      if (altBlob.size < blob.size) {
        blob = altBlob;
        currentQ = altQ;
      }
    }

    // Adaptive Pass 2 (For PNG): If still >= original, try tighter 128-color quantization
    if (blob.size >= file.size && type === 'image/png') {
      report(85, t('ضغط لوحة الألوان الذكي', 'Quantizing color palette'));
      const imgData = context.getImageData(0, 0, width, height);
      const tightBuffer = UPNG.encode([imgData.data.buffer], width, height, 128);
      const tightBlob = new Blob([tightBuffer], { type: 'image/png' });
      if (tightBlob.size < blob.size) {
        blob = tightBlob;
      }
    }

    const stripExif = Boolean(settings.stripExif);

    // If user chose NOT to strip EXIF and output is JPEG, preserve original EXIF tags
    if (!stripExif && type === 'image/jpeg') {
      try {
        const origData = await blobToDataUrl(file);
        const exifObj = piexif.load(origData);
        const hasExif = (Object.keys(exifObj['0th'] || {}).length > 0) ||
                        (Object.keys(exifObj.Exif || {}).length > 0) ||
                        (Object.keys(exifObj.GPS || {}).length > 0);
        if (hasExif) {
          const exifBytes = piexif.dump(exifObj);
          const newJpegData = await blobToDataUrl(blob);
          const withExif = piexif.insert(exifBytes, newJpegData);
          blob = dataUrlToBlob(withExif);
        }
      } catch (err) {
        console.warn('Original EXIF copy skipped:', err);
      }
    }

    // Absolute Size Guarantee:
    // Under NO circumstances should an Image Compressor output a file larger than the input!
    let isGuarded = false;
    if (blob.size >= file.size) {
      blob = file;
      isGuarded = true;
    }

    const ext = imageExtension(blob.type);
    const saved = Math.round((1 - blob.size / Math.max(file.size, 1)) * 100);
    const exifNotice = stripExif ? t('تم حذف EXIF', 'EXIF stripped') : t('تم الاحتفاظ بـ EXIF', 'EXIF retained');
    const savedNotice = isGuarded
      ? t('الأصل مضغوط مسبقاً بأعلى كفاءة', 'Original is already optimal')
      : (saved > 0 ? `${saved}% ${t('حجم أقل', 'smaller')}` : t('الحجم الأمثل', 'Optimal size'));

    report(100, t('اكتمل الضغط', 'Compression complete'));
    trackEvent('tool_completed', 'image-compressor', {
      input_bytes: file.size,
      output_bytes: blob.size,
      format: ext,
      strip_exif: stripExif,
      is_guarded: isGuarded
    });

    const finalFilename = `${imageName(file.name)}.${ext}`;

    return {
      blob,
      filename: finalFilename,
      details: `${width} × ${height} px · ${savedNotice} · ${exifNotice}`
    };
  }
});

